import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import StatsCards from "../components/StatsCards";
import RecentActivity from "../components/RecentActivity";
import PortfolioChart from "../components/PortfolioChart";
import ActiveTrades from "../components/ActiveTrades";
import QuickActions from "../components/QuickActions";
import walletService from "../services/walletService";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import DepositModal from "../components/wallet/DepositModal";
import WithdrawModal from "../components/wallet/WithdrawModal";
import TransferModal from "../components/wallet/TransferModal";
import ReferralModal from "../components/ReferralModal";
import TelegramPromptModal from "../components/Modals/TelegramPromptModal";
import BonusLine from "../components/BonusLine";

export default function Dashboard() {
  const { t } = useTranslation();
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showReferral, setShowReferral] = useState(false);

  useEffect(() => {
    loadWalletData();
  }, []);

  const loadWalletData = async () => {
    try {
      const [wallets, stats] = await Promise.all([
        walletService.getWallets(),
        walletService.getWalletStats()
      ]);
      
      let finalStats = stats;
      if ((!stats.total_balance || parseFloat(stats.total_balance) === 0) && wallets && wallets.length > 0) {
        const calculatedTotal = wallets.reduce((sum, w) => sum + (parseFloat(w.value_usdt) || 0), 0);
        if (calculatedTotal > 0) {
          finalStats = { ...stats, total_balance: calculatedTotal };
        }
      }

      setWalletData({ wallets, stats: finalStats });
    } catch (error) {
      console.error("Error loading wallet data:", error);
    } finally {
      setWalletData(prev => ({ ...prev, stats: prev?.stats || {} }));
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-[#080d1a] text-white font-inter">
        <Header />
        <div className="flex-1 flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 bg-indigo-500/5 blur-[120px] rounded-full pointer-events-none" />
          <div className="text-center animate-fade-in z-10">
            <Loader2 className="h-12 w-12 text-indigo-500 animate-spin mx-auto mb-6" />
            <h2 className="text-xl font-black text-white font-outfit uppercase tracking-widest">{t('dashboard.init_terminal')}</h2>
            <p className="mt-2 text-slate-500 font-bold text-[10px] uppercase tracking-[0.3em]">{t('dashboard.sync_data')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen text-slate-200 font-inter overflow-hidden" style={{ background: '#080d1a' }}>
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <div className="hidden md:block">
          <Sidebar />
        </div>

        <main className="flex-1 overflow-y-auto custom-scrollbar relative">
          {/* Background aurora */}
          <div className="pointer-events-none fixed inset-0 overflow-hidden">
            <div className="absolute top-[-20%] left-[10%] w-[600px] h-[600px] bg-indigo-700/8 rounded-full blur-[120px]" />
            <div className="absolute bottom-[10%] right-[5%] w-[400px] h-[400px] bg-cyan-600/6 rounded-full blur-[100px]" />
            <div className="absolute top-[40%] left-[40%] w-[300px] h-[300px] bg-purple-700/5 rounded-full blur-[80px]" />
          </div>

          <div className="relative z-10 p-4 md:p-8 max-w-7xl mx-auto pb-28 md:pb-10">

            {/* ── Page Title ── */}
            <div className="mb-6 md:mb-8">
              <h1 className="text-3xl md:text-4xl font-black text-white font-outfit tracking-tighter uppercase">
                Portfolio <span className="bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">Analytics</span>
              </h1>
              <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.25em] mt-1">Professional Asset Management</p>
            </div>

            {/* ── Stats Cards ── */}
            <div className="mb-6">
              <StatsCards walletData={walletData} />
            </div>

            {/* ── Bonus Line ── */}
            <div className="mb-6">
              <BonusLine />
            </div>

            {/* ── Main Portfolio Chart (Full Width) ── */}
            <div className="mb-6">
              <PortfolioChart
                wallets={walletData?.wallets}
                totalBalance={walletData?.stats?.total_balance || 0}
              />
            </div>

            {/* ── Bottom Grid: Active Trades + Right Column ── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Active Trades */}
              <div className="lg:col-span-2">
                <ActiveTrades />
              </div>

              {/* Right Column: Quick Actions + Recent Activity */}
              <div className="flex flex-col gap-6">
                {/* Quick Actions */}
                <div className="block">
                  <QuickActions
                    onDeposit={() => setShowDeposit(true)}
                    onWithdraw={() => setShowWithdraw(true)}
                    onTransfer={() => setShowTransfer(true)}
                    onReferral={() => setShowReferral(true)}
                  />
                </div>

                {/* Recent Activity (hidden on mobile to replace it with QuickActions) */}
                <div className="hidden md:block rounded-2xl border border-white/8 bg-gradient-to-br from-[#0d1629] via-[#0f1a2e] to-[#0a1020] p-5 shadow-[0_14px_40px_rgba(0,0,0,0.45)]">
                  <RecentActivity />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Modals */}
      <DepositModal isOpen={showDeposit} onClose={() => setShowDeposit(false)} />
      <WithdrawModal isOpen={showWithdraw} onClose={() => setShowWithdraw(false)} />
      <TransferModal isOpen={showTransfer} onClose={() => setShowTransfer(false)} />
      <ReferralModal isOpen={showReferral} onClose={() => setShowReferral(false)} />
      <TelegramPromptModal />
    </div>
  );
}